import {
  CalendarIcon,
  MapPinIcon,
  ScaleIcon,
  TrophyIcon,
} from "@heroicons/react/24/outline";
import { Card, CardBody } from "@heroui/react";
import React from "react";
import { MarketplaceCardResponseDto } from "../../../types/types";
import { formatDate } from "../../../utils/common/formatUtils";
import { getCardEventDate } from "../../../utils/deals/dealModalHelpers";
import {
  formatPurseText,
  getCurrencySymbol,
} from "../../../utils/opportunities/opportunityAndReadyRoster";
import {
  formatSportName,
  formatWeightClassName,
} from "../../../utils/utilMethods";

type OpportunityCardsListProps = {
  validCards: MarketplaceCardResponseDto[];
  selectedCard: MarketplaceCardResponseDto | null;
  handleCardClick: (card: MarketplaceCardResponseDto) => void;
  t: (key: string, options?: Record<string, any>) => string;
  cardPage: number;
  onPageChange: (page: number) => void;
  cardTotalPages: number;
};

type CardFactProps = {
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  isWarning?: boolean;
};

// One icon + text line inside an opportunity card.
const CardFact: React.FC<CardFactProps> = ({ Icon, text, isWarning }) => (
  <div
    className={`flex min-w-0 items-center gap-2 text-sm ${
      isWarning ? "text-[#EF4444]" : "text-[#71717A]"
    }`}
  >
    <Icon className="w-4 h-4 shrink-0" />
    <span className="truncate">{text}</span>
  </div>
);

const OpportunityCardsList: React.FC<OpportunityCardsListProps> = ({
  validCards,
  selectedCard,
  handleCardClick,
  t,
  cardPage,
  onPageChange,
  cardTotalPages,
}) => {
  const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {validCards.map((card) => {
          const oppCard = card.opportunityCardResponse;
          if (!oppCard) return null;

          const isSelected = selectedCard?.id === card.id;
          const eventDate = getCardEventDate(card);
          const isExpired = eventDate ? eventDate < startOfToday : false;
          const details = oppCard.formData?.details;
          const core = oppCard.formData?.core;
          const managerName =
            `${oppCard.manager?.firstName ?? ""} ${oppCard.manager?.lastName ?? ""}`.trim();
          const notAvailable = t("notAvailableShort");

          return (
            <Card
              key={card.id}
              className={`cursor-pointer rounded-2xl ${
                isSelected
                  ? "border-2 border-[#0080FF] bg-[#F4F9FF] shadow-sm"
                  : "border border-[#E4E4E7] bg-white hover:border-[#D4D4D8] shadow-[0px_1px_3px_rgba(0,0,0,0.04)]"
              }`}
              isPressable
              onPress={() => handleCardClick(card)}
            >
              <CardBody className="p-5 gap-4">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="min-w-0 truncate text-base font-bold text-[#11181C]">
                    {oppCard.eventName || managerName || notAvailable}
                  </h4>
                  <span className="shrink-0 text-base font-bold text-[#11181C]">
                    {core?.baseAmount
                      ? `${formatPurseText(core.baseAmount)} ${core.currency ? getCurrencySymbol(core.currency) : ""}`
                      : notAvailable}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <CardFact
                    Icon={CalendarIcon}
                    text={
                      eventDate
                        ? isExpired
                          ? `${formatDate(eventDate)} · ${t("expired")}`
                          : formatDate(eventDate)
                        : notAvailable
                    }
                    isWarning={isExpired}
                  />
                  <CardFact
                    Icon={TrophyIcon}
                    text={
                      details?.ruleset
                        ? formatSportName(details.ruleset)
                        : notAvailable
                    }
                  />
                  <CardFact
                    Icon={ScaleIcon}
                    text={
                      details?.weightClass
                        ? formatWeightClassName(details.weightClass)
                        : notAvailable
                    }
                  />
                  <CardFact
                    Icon={MapPinIcon}
                    text={
                      details?.location?.country ||
                      details?.location?.city ||
                      notAvailable
                    }
                  />
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {cardTotalPages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <button
            onClick={() => onPageChange(Math.max(0, cardPage - 1))}
            disabled={cardPage === 0}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            ← {t("previous")}
          </button>

          <span className="text-[13px] text-gray-500 font-medium">
            {t("pageOf", { page: cardPage + 1, total: cardTotalPages })}
          </span>

          <button
            onClick={() => onPageChange(Math.min(cardTotalPages - 1, cardPage + 1))}
            disabled={cardPage >= cardTotalPages - 1}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {t("next")} →
          </button>
        </div>
      )}
    </div>
  );
};

export default OpportunityCardsList;
